import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {UnreachableError} from '../../../libs/shared/helpers/src/types/customErrors';
import {InlineLinkButton} from '../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {openInBrowser} from '../../../srcCommon/browser/openInBrowser';
import {downloadFile} from '../../helpers/fileHelpers';
import {downloadFileAsync} from '../../services/browser';
import {
  CustomFieldUploadErrorsEnum,
  DownloadTemplateLink,
  DownloadTemplateLinkTypesEnum,
} from './settingsCustomFieldAttributeImportCsvTypes';

/*
 * Props.
 */

interface SettingsCustomFieldAttributeImportErrorProps {
  requiredColumns: ReadonlyArray<string>;
  error: CustomFieldUploadErrorsEnum;
  row: number | undefined;
  fileWithErrors: File | undefined;
  downloadCsvLink: DownloadTemplateLink;
}

/*
 * Style.
 */

const StyledErrorDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};

  a {
    color: ${(p) => p.theme.palette.red.shade40};
    font-weight: ${fontWeights.semibold};
    text-decoration: underline;
  }

  &:not(:first-child) {
    margin-top: 8px;
  }
`;

const StyledDownloadFileSpan = styled.span`
  text-decoration: underline;
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const SettingsCustomFieldAttributeImportError: FC<SettingsCustomFieldAttributeImportErrorProps> = (
  props,
) => <StyledErrorDiv>{renderErrorMessage(props)}</StyledErrorDiv>;

function renderErrorMessage(props: SettingsCustomFieldAttributeImportErrorProps) {
  const {error, row, fileWithErrors, downloadCsvLink, requiredColumns} = props;

  switch (error) {
    case CustomFieldUploadErrorsEnum.NO_HEADER:
      return (
        <>
          <Trans id="custom-field-attribute-import-dialog-error-no-header">
            No header row was found. Required columns: {requiredColumns.map((c) => `”${c}”`).join(', ')}.
          </Trans>{' '}
          {renderDownloadCSVLink(downloadCsvLink)}
        </>
      );

    case CustomFieldUploadErrorsEnum.MISSING_HEADER_COLUMN:
      return (
        <>
          <Trans id="custom-field-attribute-import-dialog-error-missing-header-column">
            At least one header column is missing. Required columns:{' '}
            {requiredColumns.map((c) => `”${c}”`).join(', ')}.
          </Trans>{' '}
          {renderDownloadCSVLink(downloadCsvLink)}
        </>
      );

    case CustomFieldUploadErrorsEnum.UNRECOGNIZED_HEADERS:
      return (
        <Trans id="custom-field-attribute-import-dialog-error-unrecognized-header">
          An unrecognized column was found. Please verify your custom field names, they are case-sensitive.
        </Trans>
      );

    // TODO: Remove this when we pass it as MISSING_NAME instead of MISSING_FIELDS
    case CustomFieldUploadErrorsEnum.MISSING_FIELDS:
    case CustomFieldUploadErrorsEnum.MISSING_NAME:
      return (
        <Trans id="custom-field-attribute-import-dialog-missing-fields">
          Error on row {row}: Name must be included.
        </Trans>
      );

    case CustomFieldUploadErrorsEnum.TOO_MANY_ROWS:
      return (
        <Trans id="custom-field-attribute-import-dialog-error-too-many-rows">
          The uploaded file exceeds the maximum size of 3000 rows.
        </Trans>
      );

    case CustomFieldUploadErrorsEnum.ROWS_ERRORS_WITH_DOWNLOAD:
      return renderDownloadUpdatedCSV(fileWithErrors);

    case CustomFieldUploadErrorsEnum.EMPTY_HEADERS:
      return (
        <>
          <Trans id="custom-field-attribute-import-error-empty-header">A column heading is empty.</Trans>{' '}
          {renderDownloadCSVLink(downloadCsvLink)}
        </>
      );

    case CustomFieldUploadErrorsEnum.PARSE_ERROR_MORE_ROWS_THAN_HEADER:
      return (
        <Trans id="custom-field-attribute-import-parse-error-more-rows-than-header">
          A row was found with more rows than specified in the header columns.
        </Trans>
      );

    case CustomFieldUploadErrorsEnum.INVALID_DUPLICATE_HEADERS:
      return (
        <Trans id="custom-field-attribute-import-error-invalid-duplicate-headers">
          Duplicate columns were found. Please remove the duplicate columns.
        </Trans>
      );

    case CustomFieldUploadErrorsEnum.INVALID_FILE_TYPE:
      return (
        <Trans id="custom-field-attribute-import-invalid-file-type">
          The uploaded file is not a .csv file.
        </Trans>
      );

    case CustomFieldUploadErrorsEnum.INVALIDATE_DUPLICATE_HEADERS:
      return (
        <Trans id="custom-field-attribute-import-error-duplicate-header">
          A column has been included more than once.
        </Trans>
      );

    case CustomFieldUploadErrorsEnum.PARSE_ERROR:
      return (
        <Trans id="custom-field-attribute-import-error-parse">
          There are more columns in rows than in headers.
        </Trans>
      );

    case CustomFieldUploadErrorsEnum.INVALID_CUSTOM_FIELD_VALUE:
      return (
        <Trans id="custom-field-attribute-import-error-invalid-value">
          An incorrect value was entered for a custom field.
        </Trans>
      );

    case CustomFieldUploadErrorsEnum.DUPLICATE_IDENTIFIER:
      return (
        <Trans id="custom-field-attribute-import-error-duplicate-identifier">
          The same resource is being updated twice.
        </Trans>
      );

    default:
      return <Trans id="custom-field-attribute-import-error-unknown">An unknown error has occurred.</Trans>;
  }
}

function renderDownloadCSVLink(link: DownloadTemplateLink) {
  const onClick = () => {
    switch (link.type) {
      case DownloadTemplateLinkTypesEnum.LINK:
        openInBrowser(link.url);
        return;

      case DownloadTemplateLinkTypesEnum.FILE:
        downloadFileAsync(link.url, link.name);
        return;

      default:
        throw new UnreachableError(link);
    }
  };

  return (
    <InlineLinkButton onClick={onClick}>
      <Trans id="custom-field-attribute-import-download-csv-template">
        Make sure you&apos;ve downloaded the CSV template
      </Trans>
    </InlineLinkButton>
  );
}

function renderDownloadUpdatedCSV(file: File | undefined) {
  return (
    <>
      <Trans id="custom-field-attribute-import-errors-found">Errors found.</Trans>{' '}
      {file && (
        <>
          <StyledDownloadFileSpan onClick={() => downloadFile(document, file)}>
            <Trans id="custom-field-attribute-import-errors-found-download-updated-csv">
              Download the updated CSV
            </Trans>
          </StyledDownloadFileSpan>{' '}
          <Trans id="custom-field-attribute-import-errors-found-to-view">to view errors.</Trans>
        </>
      )}
    </>
  );
}
